import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Archive, Clock, Download, File as FileIcon, FileText, Film, Image as ImageIcon, Lock, Music, Package } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { setSeoMetadata } from '../lib/seo';
import BackgroundSlideshow from '../tools/transfer/BackgroundSlideshow';
import { useSiteFeatureFlags } from '../hooks/useSiteFeatureFlags';
import { useTransferTheme } from '../tools/transfer/theme';
import { backgroundUrl, fileUrl, formatBytes, getSlides, getTransfer, unlockTransfer, zipUrl } from '../tools/transfer/api';

// A small colour-coded tile per file type, so a list of files can be scanned at a glance.
const KINDS = [
  { test: /\.(png|jpe?g|gif|webp|svg|bmp|heic|avif)$/i, icon: ImageIcon, tone: 'bg-pink-100 text-pink-600' },
  { test: /\.(mp4|mov|avi|mkv|webm|m4v)$/i, icon: Film, tone: 'bg-purple-100 text-purple-600' },
  { test: /\.(mp3|wav|flac|aac|m4a|ogg)$/i, icon: Music, tone: 'bg-emerald-100 text-emerald-600' },
  { test: /\.(zip|rar|7z|tar|gz|tgz)$/i, icon: Archive, tone: 'bg-amber-100 text-amber-600' },
  { test: /\.(pdf|docx?|xlsx?|pptx?|txt|rtf|csv|md)$/i, icon: FileText, tone: 'bg-sky-100 text-sky-600' },
];
const kindOf = (name) => KINDS.find((k) => k.test.test(name)) || { icon: FileIcon, tone: 'bg-slate-100 text-slate-500' };

const expiryText = (iso) => {
  if (!iso) return 'Never expires';
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (days <= 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  if (days <= 14) return `Expires in ${days} days`;
  return `Expires ${new Date(iso).toLocaleDateString(undefined, { dateStyle: 'long' })}`;
};

const TransferDownloadPage = () => {
  const { token } = useParams();
  const [dark] = useTransferTheme();
  const { transfer_site_enabled } = useSiteFeatureFlags(); // the site template header is in the page flow; the CRM one is fixed over it
  const [transfer, setTransfer] = useState(null);
  const [gone, setGone] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [slides, setSlides] = useState([]);
  const [seconds, setSeconds] = useState(6);

  // Private link: must never be indexed.
  useEffect(() => {
    setSeoMetadata({ title: 'Download files', noIndex: true });
  }, []);

  useEffect(() => {
    getTransfer(token).then(setTransfer).catch(() => setGone(true));
  }, [token]);

  // The same client slideshow as the landing page, unless the sender (paid) set their own background.
  useEffect(() => {
    getSlides().then((d) => { setSlides(d.slides); setSeconds(d.slide_seconds); }).catch(() => {});
  }, []);

  const unlock = async (e) => {
    e.preventDefault();
    setError('');
    try {
      setTicket(await unlockTransfer(token, password));
    } catch (err) {
      setError(err.response?.status === 429 ? 'Too many attempts. Try again later.' : 'Incorrect password.');
    }
  };

  const locked = transfer?.password_required && !ticket;
  const files = transfer?.files || [];
  const ink = dark ? 'text-white' : 'text-slate-900';
  const soft = dark ? 'text-white/60' : 'text-slate-500';
  const card = dark ? 'bg-[#26251f]/95 border-white/10' : 'bg-white/95 border-slate-200';
  const chip = dark ? 'bg-white/10 text-white/80' : 'bg-slate-100 text-slate-600';
  const row = dark ? 'bg-white/5 hover:bg-white/10' : 'bg-slate-50 hover:bg-slate-100';
  const primary = 'w-full inline-flex items-center justify-center gap-2 min-h-[56px] rounded-2xl bg-[#FF6B00] hover:bg-[#e65f00] text-white text-lg font-semibold shadow-lg shadow-orange-500/25 transition-colors';

  return (
    <div className={`min-h-screen relative ${dark ? 'bg-slate-950' : 'bg-slate-100'}`}>
      {transfer?.has_background ? (
        <>
          <img src={backgroundUrl(token)} alt="" aria-hidden="true" className="fixed inset-0 w-full h-full object-cover" data-testid="transfer-background" />
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </>
      ) : slides.length ? (
        <div className="fixed inset-0">
          <BackgroundSlideshow slides={slides} seconds={seconds} dark={dark} />
        </div>
      ) : (
        <div className={`fixed inset-0 ${dark ? 'bg-gradient-to-br from-slate-900 via-slate-950 to-indigo-950' : 'bg-gradient-to-br from-indigo-50 via-white to-orange-50'}`} />
      )}

      <div className="relative">
        <Header />
        <main className={`${transfer_site_enabled ? 'pt-10' : 'pt-32'} pb-24 px-4`}>
          <div className="max-w-xl mx-auto">
            {gone && (
              <div className={`${card} border rounded-3xl shadow-2xl p-8 text-center`} data-testid="transfer-gone">
                <div className="mx-auto w-14 h-14 rounded-2xl bg-slate-100 text-slate-400 flex items-center justify-center mb-4"><Clock className="w-7 h-7" /></div>
                <h1 className={`text-2xl font-bold mb-2 ${ink}`}>This link is no longer available</h1>
                <p className={soft}>It may have expired, been deleted, or reached its download limit. Ask the sender to send it again.</p>
              </div>
            )}

            {transfer && (
              <div className={`${card} border rounded-3xl shadow-2xl backdrop-blur p-6 sm:p-8`}>
                <div className="flex items-start gap-4 mb-5">
                  <div className="shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF8A3D] to-[#FF6B00] text-white flex items-center justify-center shadow-md">
                    <Package className="w-7 h-7" />
                  </div>
                  <div className="min-w-0">
                    <h1 className={`text-2xl font-bold leading-tight break-words ${ink}`}>{transfer.title || 'Your files are ready'}</h1>
                    {transfer.sender_name && <p className={`text-sm mt-1 ${soft}`}>Sent by <span className={`font-medium ${ink}`}>{transfer.sender_name}</span></p>}
                  </div>
                </div>

                {transfer.message && (
                  <p className={`mb-5 whitespace-pre-line text-sm leading-relaxed rounded-xl border-l-4 border-[#FF6B00] px-4 py-3 ${dark ? 'bg-white/5 text-white/90' : 'bg-slate-50 text-slate-700'}`}>
                    {transfer.message}
                  </p>
                )}

                <div className="flex flex-wrap gap-2 mb-6 text-xs font-medium">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${chip}`}>
                    {files.length} file{files.length !== 1 ? 's' : ''} · {formatBytes(transfer.total_bytes)}
                  </span>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${chip}`}><Clock className="w-3.5 h-3.5" /> {expiryText(transfer.expires_at)}</span>
                  {transfer.password_required && (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${chip}`}><Lock className="w-3.5 h-3.5" /> Password protected</span>
                  )}
                </div>

                {locked ? (
                  <form onSubmit={unlock} className="space-y-3" data-testid="transfer-unlock">
                    <label className="block text-sm">
                      <span className={`inline-flex items-center gap-2 font-medium ${ink}`}><Lock className="w-4 h-4" /> Enter the password to unlock these files</span>
                      <input type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)}
                        className={`mt-2 w-full rounded-xl border px-4 min-h-[48px] ${dark ? 'bg-black/30 border-white/15 text-white' : 'bg-white border-slate-300 text-slate-900'}`} />
                    </label>
                    {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
                    <button type="submit" className={primary}>Unlock</button>
                  </form>
                ) : (
                  <>
                    {files.length > 1 ? (
                      <a href={zipUrl(token, ticket)} download className={`${primary} mb-5`} data-testid="transfer-download-all">
                        <Download className="w-5 h-5" /> Download all ({files.length} files, .zip)
                      </a>
                    ) : files.length === 1 ? (
                      <a href={fileUrl(token, files[0].id, ticket)} download className={`${primary} mb-5`} data-testid="transfer-download-one">
                        <Download className="w-5 h-5" /> Download
                      </a>
                    ) : null}

                    <ul className="space-y-2">
                      {files.map((f) => {
                        const kind = kindOf(f.name);
                        const Icon = kind.icon;
                        return (
                          <li key={f.id} className={`${row} rounded-xl px-3 py-2.5 flex items-center gap-3 transition-colors`}>
                            <span className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${kind.tone}`}><Icon className="w-5 h-5" /></span>
                            <span className="min-w-0 flex-1">
                              <span className={`block truncate text-sm font-medium ${ink}`} title={f.path || f.name}>{f.path || f.name}</span>
                              <span className={`block text-xs ${soft}`}>{formatBytes(f.size)}</span>
                            </span>
                            <a href={fileUrl(token, f.id, ticket)} download aria-label={`Download ${f.name}`}
                              className={`shrink-0 inline-flex items-center gap-1.5 px-3 min-h-[40px] rounded-full text-sm font-medium border ${dark ? 'border-white/20 text-white hover:bg-white/10' : 'border-slate-300 text-slate-700 hover:bg-white'}`}>
                              <Download className="w-4 h-4" /> <span className="hidden sm:inline">Download</span>
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default TransferDownloadPage;
